import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader2, MapPin, ChevronDown, ChevronRight, Route } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import type { DirectDeliveryOrder, WizardDelivery, CallOffDirectDeliverySource } from '../../../lib/api';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Progress } from '../../ui/progress';
import { Checkbox } from '../../ui/checkbox';
import { toast } from 'sonner';
import LocalColumnManager from './LocalColumnManager';
import { DEFAULT_DIRECT_COLUMNS, type LocalColumn } from './DirectDeliveryColumns';
import { useCallOffConfig } from '../../../hooks/useCallOffConfig';
import { useCommodityTypeOptions } from './useCommodityTypeOptions';

/**
 * Resolve the source code for the supplier this call-off is being done for,
 * by matching the supplier name against configured direct-delivery sources.
 * The call-off is tied to a specific supplier so the direct-delivery list
 * should be scoped to that supplier's source rather than letting the user
 * pick any source.
 */
function supplierToSourceCode(
  supplierName: string | undefined,
  sources: CallOffDirectDeliverySource[],
): string {
  if (!supplierName) return '';
  const upper = supplierName.toUpperCase();
  const match = sources.find((s) => {
    if (!s.is_active) return false;
    const token = (s.supplier_match_token || '').trim().toUpperCase();
    return token && upper.includes(token);
  });
  return match ? match.code : '';
}

interface DirectDeliveryModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (deliveries: WizardDelivery[]) => void;
  supplierId: number;
  supplierName?: string;
  existingFillPercent: number;
  editDelivery?: WizardDelivery;
}

function useLocalColumns(defaults: LocalColumn[]) {
  const [columns, setColumns] = useState<LocalColumn[]>(defaults);

  const toggle = useCallback((columnId: string) => {
    setColumns(prev => prev.map(c =>
      c.id === columnId ? { ...c, visible: !c.visible } : c
    ));
  }, []);

  const moveUp = useCallback((columnId: string) => {
    setColumns(prev => {
      const sorted = [...prev].sort((a, b) => a.sortOrder - b.sortOrder);
      const idx = sorted.findIndex(c => c.id === columnId);
      if (idx <= 0) return prev;
      const newOrder = sorted.map((col, i) => {
        if (i === idx) return { ...col, sortOrder: i - 1 };
        if (i === idx - 1) return { ...col, sortOrder: i + 1 };
        return { ...col, sortOrder: i };
      });
      return newOrder;
    });
  }, []);

  const visible = useMemo(
    () => [...columns].sort((a, b) => a.sortOrder - b.sortOrder).filter(c => c.visible),
    [columns]
  );

  return { columns, visible, toggle, moveUp };
}

/** Get a cell value from an order based on column id */
function getOrderCellValue(order: DirectDeliveryOrder, colId: string): string {
  switch (colId) {
    case 'delivery_date':
      return order.delivery_date
        ? new Date(order.delivery_date + 'T00:00:00').toLocaleDateString()
        : '-';
    case 'item_code': {
      if (order.products.length === 1) return order.products[0].code;
      return order.products.map(p => p.code).join(', ');
    }
    case 'description': {
      if (order.products.length === 1) return order.products[0].desc;
      return order.products[0].desc + (order.products.length > 1 ? ` (+${order.products.length - 1})` : '');
    }
    case 'ship_to_address': return order.address || '-';
    case 'postcode': return order.postcode || '-';
    case 'bp_reference': return order.bp_reference || '-';
    case 'creation_date':
      return order.creation_date
        ? new Date(order.creation_date + 'T00:00:00').toLocaleDateString()
        : '-';
    case 'date_updated':
      return order.date_updated
        ? new Date(order.date_updated + 'T00:00:00').toLocaleDateString()
        : '-';
    case 'drop_number': return order.drop_number || '-';
    case 'so_number': return order.id;
    case 'customer': return order.customer || '-';
    case 'fill_percent': return `${order.fill_percent}%`;
    default: return '-';
  }
}

export default function DirectDeliveryModal({
  open, onClose, onAdd, supplierId, supplierName, existingFillPercent, editDelivery,
}: DirectDeliveryModalProps) {
  const [orders, setOrders] = useState<DirectDeliveryOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(new Set());

  // Per-customer configuration (sources list + routing origin).
  const { config } = useCallOffConfig();

  // Filters — source code is locked to the call-off's supplier so the user
  // can't accidentally pull orders for a different supplier.
  const lockedFactoryCode = useMemo(
    () => supplierToSourceCode(supplierName, config.direct_delivery_sources),
    [supplierName, config.direct_delivery_sources],
  );
  const [searchFilter, setSearchFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Client-side row filters (applied at the order level — an order is kept if
  // it has at least one product passing the active filters). "hideTestParts"
  // mirrors the supplier-side Current Stock filter; "commodityFilter" narrows
  // to a single commodity type ("all" = no filter).
  const [hideTestParts, setHideTestParts] = useState(false);
  const [commodityFilter, setCommodityFilter] = useState('all');

  // Column management
  const { columns, visible: visibleCols, toggle: toggleCol, moveUp: moveColUp } = useLocalColumns(DEFAULT_DIRECT_COLUMNS);

  const hasPrefilledRef = useRef(false);

  const fetchOrders = useCallback(async () => {
    // No matching active source for this supplier → show no orders rather
    // than letting the request omit the factory_filter and surface every
    // customer's items. Source matching is the only scoping mechanism for
    // direct-delivery rows, so an unmatched supplier means "not configured".
    if (!lockedFactoryCode) {
      setOrders([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        supplier_id: supplierId,
        factory_filter: lockedFactoryCode,
      };
      if (dateFrom) params['date_from'] = dateFrom;
      if (dateTo) params['date_to'] = dateTo;
      if (searchFilter) params['search'] = searchFilter;

      const res = await callOffAPI.getDirectDeliveryOrders(params);
      setOrders(res.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load direct delivery orders';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [supplierId, dateFrom, dateTo, searchFilter, lockedFactoryCode]);

  useEffect(() => {
    if (open) { hasPrefilledRef.current = false; }
  }, [open]);

  useEffect(() => {
    if (open) { fetchOrders(); }
  }, [open, fetchOrders]);

  // Pre-fill selected orders when editing
  useEffect(() => {
    if (!open || hasPrefilledRef.current || orders.length === 0) return;
    if (editDelivery) {
      if (editDelivery.salesOrderId) {
        setSelectedOrderIds(new Set([editDelivery.salesOrderId]));
        setExpandedOrderIds(new Set([editDelivery.salesOrderId]));
      } else {
        const editAddr = (editDelivery.address || '').trim().toLowerCase();
        const editPostcode = (editDelivery.postcode || '').trim().toLowerCase();
        const matched = orders.find(o => {
          const oAddr = (o.address || '').trim().toLowerCase();
          const oPostcode = (o.postcode || '').trim().toLowerCase();
          return editPostcode && oPostcode
            ? oAddr === editAddr && oPostcode === editPostcode
            : oAddr === editAddr;
        });
        if (matched) {
          setSelectedOrderIds(new Set([matched.id]));
          setExpandedOrderIds(new Set([matched.id]));
        }
      }
      hasPrefilledRef.current = true;
    } else {
      setSelectedOrderIds(new Set());
      hasPrefilledRef.current = true;
    }
  }, [open, editDelivery, orders]);

  const toggleOrder = (orderId: string) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleExpanded = (orderId: string) => {
    setExpandedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allVisibleSelected = visibleOrders.length > 0
      && visibleOrders.every(o => selectedOrderIds.has(o.id));
    if (allVisibleSelected) setSelectedOrderIds(new Set());
    else setSelectedOrderIds(new Set(visibleOrders.map(o => o.id)));
  };

  // Commodity types carried by the loaded orders. Merged with the configured
  // list in the hook below, so the dropdown offers every commodity type rather
  // than only those that happen to appear on screen.
  const commodityTypesInRows = useMemo(() => {
    const set = new Set<string>();
    for (const o of orders) {
      for (const p of o.products) {
        if (p.commodity_type) set.add(p.commodity_type);
      }
    }
    return Array.from(set);
  }, [orders]);

  const commodityOptions = useCommodityTypeOptions(commodityTypesInRows);

  // Orders after the hide-"part" and commodity filters. An order is kept when
  // at least one of its products passes the active filters.
  const visibleOrders = useMemo(
    () => orders.filter(o => {
      const filterActive = hideTestParts || commodityFilter !== 'all';
      if (!filterActive) return true;
      return o.products.some(p => {
        if (
          hideTestParts &&
          ((p.code || '').toLowerCase().startsWith('part') ||
            (p.desc || '').toLowerCase().startsWith('part'))
        ) return false;
        if (commodityFilter !== 'all' && (p.commodity_type || '') !== commodityFilter) return false;
        return true;
      });
    }),
    [orders, hideTestParts, commodityFilter]
  );

  const selectedOrders = useMemo(
    () => orders.filter(o => selectedOrderIds.has(o.id)),
    [orders, selectedOrderIds]
  );

  const modalFillPercent = useMemo(() => {
    let fill = 0;
    for (const order of selectedOrders) {
      for (const product of order.products) {
        if (product.load_qty > 0) fill += product.qty / product.load_qty;
      }
    }
    return Math.round(fill * 100);
  }, [selectedOrders]);

  const totalFillPercent = existingFillPercent + modalFillPercent;
  const totalUnits = selectedOrders.reduce(
    (sum, order) => sum + order.products.reduce((s, p) => s + p.qty, 0), 0
  );

  const handleAdd = () => {
    if (selectedOrders.length === 0) { toast.error('Please select at least one order'); return; }
    const deliveries: WizardDelivery[] = selectedOrders.map(order => ({
      type: 'direct' as const,
      destinationId: null,
      destinationName: order.customer,
      salesOrderId: order.id,
      address: order.address,
      postcode: order.postcode,
      deliveryDate: order.delivery_date || '',
      parts: order.products.map(p => ({
        partId: null,
        partNumber: p.code,
        description: p.desc,
        qty: p.qty,
        palletQty: 0,
        loadQty: p.load_qty,
      })),
    }));
    onAdd(deliveries);
  };

  const handleClose = () => {
    setSelectedOrderIds(new Set());
    setExpandedOrderIds(new Set());
    setSearchFilter('');
    setDateFrom('');
    setDateTo('');
    setHideTestParts(false);
    setCommodityFilter('all');
    hasPrefilledRef.current = false;
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full flex flex-col p-0">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <MapPin className="h-5 w-5 text-blue-500" />
          <h2 className="text-lg font-semibold">
            {editDelivery ? 'Edit Direct Delivery' : 'Add Direct Deliveries'}
          </h2>
        </div>

        {/* Filters toolbar */}
        <div className="flex items-end gap-4 px-6 py-3 border-b bg-muted/30 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs">Search</Label>
            <Input className="w-48 h-8" placeholder="SO#, Customer, Part..."
              value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Supplier</Label>
            <div
              className="w-44 h-8 text-sm border border-border rounded-lg px-2 bg-muted flex items-center font-medium"
              title="Locked to the call-off supplier"
            >
              {supplierName || 'Supplier'}
              {lockedFactoryCode && (
                <span className="ml-1 text-xs text-muted-foreground">({lockedFactoryCode})</span>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Date From</Label>
            <Input type="date" className="w-36 h-8"
              value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Date To</Label>
            <Input type="date" className="w-36 h-8"
              value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Parts</Label>
            <select
              value={hideTestParts ? 'hide_part' : 'all'}
              onChange={(e) => setHideTestParts(e.target.value === 'hide_part')}
              className="w-40 h-8 text-sm border border-border rounded-lg px-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Row filter"
            >
              <option value="all">Show all parts</option>
              <option value="hide_part">Hide &ldquo;part&rdquo;-prefixed</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Commodity Type</Label>
            <select
              value={commodityFilter}
              onChange={(e) => setCommodityFilter(e.target.value)}
              className="w-40 h-8 text-sm border border-border rounded-lg px-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label="Commodity type filter"
            >
              <option value="all">All commodity types</option>
              {commodityOptions.map(ct => (
                <option key={ct} value={ct}>{ct}</option>
              ))}
            </select>
          </div>
          <LocalColumnManager columns={columns} onToggle={toggleCol} onMoveUp={moveColUp} />
          {config.routing_origin && selectedOrders.length > 0 && selectedOrders.some(o => o.postcode) && (
            <Button variant="outline" size="sm" className="gap-1.5"
              onClick={() => {
                const postcodes = selectedOrders.map(o => o.postcode).filter(Boolean);
                if (postcodes.length === 0) return;
                const start = encodeURIComponent(config.routing_origin);
                const waypoints = postcodes.map(pc => encodeURIComponent(pc)).join('/');
                window.open(`https://www.google.com/maps/dir/${start}/${waypoints}`, '_blank');
              }}>
              <Route className="h-3.5 w-3.5" />
              Plan Route
            </Button>
          )}
          <div className="flex-1" />
          <div className="text-right space-y-1">
            <Label className="text-xs">Container Fill</Label>
            <div className="flex items-center gap-2">
              <Progress value={Math.min(totalFillPercent, 100)} className="w-32 h-2" />
              <span className={`text-sm font-bold ${totalFillPercent > 90 ? 'text-red-600' : totalFillPercent > 50 ? 'text-orange-600' : 'text-green-600'}`}>
                {totalFillPercent}%
              </span>
            </div>
            {!editDelivery && existingFillPercent > 0 && (
              <p className="text-xs text-muted-foreground">
                Existing: {existingFillPercent}% + This: {modalFillPercent}%
              </p>
            )}
          </div>
        </div>

        {/* Orders table */}
        <div className="flex-1 overflow-auto px-6 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              {lockedFactoryCode
                ? 'No direct delivery orders found for the selected criteria.'
                : 'No direct-delivery source is configured for this supplier. Ask an admin to add a matching source under Admin → Call-Off → Direct Delivery Sources.'}
            </div>
          ) : visibleOrders.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground text-sm">
              All {orders.length} orders were hidden by the active filters.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-20 bg-background border-b">
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="py-2 px-2 w-8" />
                  <th className="py-2 px-2 w-8">
                    <Checkbox
                      checked={visibleOrders.length > 0 && visibleOrders.every(o => selectedOrderIds.has(o.id))}
                      onCheckedChange={toggleSelectAll}
                    />
                  </th>
                  {visibleCols.map(col => (
                    <th key={col.id} className={`py-2 px-2 font-medium whitespace-nowrap ${col.id === 'fill_percent' ? 'text-right' : ''}`}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {visibleOrders.map(order => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    visibleCols={visibleCols}
                    isSelected={selectedOrderIds.has(order.id)}
                    isExpanded={expandedOrderIds.has(order.id)}
                    onToggleSelect={() => toggleOrder(order.id)}
                    onToggleExpand={() => toggleExpanded(order.id)}
                    routingOrigin={config.routing_origin}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t bg-muted/30">
          <span className="text-sm text-muted-foreground">
            {selectedOrders.length} {selectedOrders.length === 1 ? 'order' : 'orders'} selected &middot;{' '}
            {totalUnits} units
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button onClick={handleAdd} disabled={selectedOrders.length === 0}>
              {editDelivery ? 'Update Deliveries' : 'Add Deliveries to Call-Off'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface OrderRowProps {
  order: DirectDeliveryOrder;
  visibleCols: LocalColumn[];
  isSelected: boolean;
  isExpanded: boolean;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
  routingOrigin: string;
}

function OrderRow({ order, visibleCols, isSelected, isExpanded, onToggleSelect, onToggleExpand, routingOrigin }: OrderRowProps) {
  return (
    <>
      <tr className={`hover:bg-muted/50 ${isSelected ? 'bg-blue-50' : ''}`}>
        <td className="py-2 px-2">
          <button onClick={onToggleExpand} className="text-muted-foreground hover:text-foreground">
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
        <td className="py-2 px-2">
          <Checkbox checked={isSelected} onCheckedChange={onToggleSelect} />
        </td>
        {visibleCols.map(col => (
          <td key={col.id} className={`py-2 px-2 ${col.id === 'fill_percent' ? 'text-right' : ''} ${col.id === 'ship_to_address' || col.id === 'description' ? 'text-xs truncate max-w-[200px]' : ''} ${col.id === 'item_code' || col.id === 'drop_number' || col.id === 'so_number' ? 'font-mono text-xs' : ''}`}>
            {col.id === 'postcode' && order.postcode ? (
              routingOrigin ? (
                <a
                  href={`https://www.google.com/maps/dir/${encodeURIComponent(routingOrigin)}/${encodeURIComponent(order.postcode)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                  title={`Directions from ${routingOrigin} to ${order.postcode}`}
                >
                  <MapPin className="h-3 w-3" />
                  {order.postcode}
                </a>
              ) : (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3 text-muted-foreground" />
                  {order.postcode}
                </span>
              )
            ) : col.id === 'fill_percent' ? (
              <span className={`font-medium ${order.fill_percent > 90 ? 'text-red-600' : order.fill_percent > 50 ? 'text-orange-600' : 'text-green-600'}`}>
                {order.fill_percent}%
              </span>
            ) : (
              getOrderCellValue(order, col.id)
            )}
          </td>
        ))}
      </tr>
      {isExpanded && order.products.map((product, idx) => (
        <tr key={`${order.id}-${idx}`} className="bg-muted/20">
          <td className="py-1 px-2" />
          <td className="py-1 px-2" />
          <td className="py-1 px-2" colSpan={Math.ceil(visibleCols.length / 2)}>
            <span className="text-xs font-mono">{product.code}</span>
            <span className="text-xs text-muted-foreground ml-3">{product.desc}</span>
          </td>
          <td className="py-1 px-2 text-right text-xs" colSpan={Math.floor(visibleCols.length / 2)}>
            Qty: {product.qty} &middot; Load Qty: {product.load_qty}
          </td>
        </tr>
      ))}
    </>
  );
}
